import { Editor as EditorClass } from '@tiptap/core';
import capitalize from 'lodash/capitalize';
import React, { useEffect, useRef } from 'react';
import { useLatest } from 'react-use';

import { CopyRichTextButton, useTypingThrottle } from '@eluve/blocks';
import {
  Card,
  CardContent,
  CardHeader,
  LoadingIcon,
  Skeleton,
  Stack,
  cn,
} from '@eluve/components';
import { EluveEditor, EluveEditorHandle } from '@eluve/eluve-editor';
import { getTiptapMarkdownContentFromEditor } from '@eluve/markdown-utils';
import { useAnalytics } from '@eluve/posthog-react';
import { areStringsEqualWithoutSpecialCharacters } from '@eluve/utils';
import { AppointmentSummaryKeys } from '@eluve/utils';

export type SummarySectionMarkdownAction = React.FC<{
  getSummary: () => string | null | undefined;
}>;

export interface SummarySectionMarkdownProps {
  className?: string;
  summaryKey: AppointmentSummaryKeys;
  sectionTitle: string;
  content?: string;
  loading?: boolean;
  handleContentChange: (
    key: AppointmentSummaryKeys,
    newContent: string,
  ) => void;
  disabled: boolean;
  additionalActions?: SummarySectionMarkdownAction[];
  flat?: boolean;
  compact?: boolean;
}

export const SummarySectionMarkdown: React.FC<SummarySectionMarkdownProps> = ({
  className,
  summaryKey,
  sectionTitle: title,
  content,
  loading,
  handleContentChange,
  disabled,
  additionalActions,
  flat,
  compact,
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<EluveEditorHandle | null>(null);
  const latestContent = useLatest(content);
  const analytics = useAnalytics('appointment-summary-section');

  const throttledHandleContentChange = useTypingThrottle(handleContentChange);

  const onEditorContentUpdated = (editor?: EditorClass | undefined): void => {
    if (editor) {
      if (
        areStringsEqualWithoutSpecialCharacters(
          latestContent.current,
          getTiptapMarkdownContentFromEditor(editor),
        )
      ) {
        return;
      }
      const content = getTiptapMarkdownContentFromEditor(editor);
      throttledHandleContentChange(summaryKey, content);
    }
  };

  useEffect(() => {
    editorRef?.current?.setEditable(!loading && !disabled);
  }, [editorRef, disabled, loading]);

  const showEditor = Boolean(!loading || (loading && content));

  return (
    // eslint-disable-next-line better-tailwindcss/no-unknown-classes
    <div className={cn('viewer [&_div[id^=tippy-]]:z-20!', className)}>
      <Card
        className={cn(
          'w-full rounded-xl border-border-primary',
          flat && 'rounded-none border-none bg-transparent shadow-none',
        )}
      >
        <CardHeader
          className={cn(
            'flex h-10 flex-row place-items-center justify-between rounded-t-xl border-b bg-bg-primary p-0 px-5 tracking-normal',
            flat && 'rounded-none border-none bg-transparent px-0',
          )}
        >
          <Stack direction="horizontal" fullWidth={false} gap={compact ? 1 : 2}>
            <h3 className="text-sm font-semibold text-text-primary">
              <b className="font-semibold">
                {title
                  .split('_')
                  .map((s) => capitalize(s))
                  .join(' ')}
              </b>
            </h3>
            {!loading && Boolean(content?.length) && (
              <>
                <CopyRichTextButton
                  size={compact ? 'sm' : undefined}
                  getContentElement={() => contentRef.current}
                  hideContentInTooltip
                  markdownContent={content}
                  onCopy={() => {
                    analytics.capture('appointment.note_section_copied', {
                      section_name: summaryKey,
                    });
                  }}
                />
                {additionalActions?.map((Action, i) => (
                  <Action key={i} getSummary={() => latestContent.current} />
                ))}
              </>
            )}
          </Stack>
          {loading && (
            <LoadingIcon size="lg" className="text-border-accent-quaternary" />
          )}
        </CardHeader>
        <hr className="sr-only" />
        <CardContent
          className={cn(
            'flex flex-col justify-between gap-5 rounded-b-xl bg-white p-4',
            flat && 'rounded-none bg-transparent p-0 [&_.ProseMirror]:p-0!',
          )}
          ref={contentRef}
        >
          {loading && !content && (
            <>
              <Skeleton className="h-3" />
              <Skeleton className="h-3" />
            </>
          )}

          {showEditor && (
            <EluveEditor
              ref={editorRef}
              content={content}
              onUpdate={onEditorContentUpdated}
            />
          )}
        </CardContent>
      </Card>
      <br className="sr-only" />
    </div>
  );
};
